import classNames from 'classnames';
import styles from './Article.module.scss';
import { HeroCard, HeroCardProps } from 'ui/components/2-molecules/Card/HeroCard/HeroCard';
import { TopLevelGrid } from '../TopLevelGrid/TopLevelGrid';
import { TopLevelGridCell } from '../TopLevelGrid/TopLevelGridCell';
import { ArticleAuthor, ArticleAuthorProps } from 'ui/components/2-molecules/Article/ArticleAuthor/ArticleAuthor';
import { ArticleInfo, ArticleInfoProps } from 'ui/components/2-molecules/Article/ArticleInfo/ArticleInfo';
import { RichText } from 'ui/components/1-atoms/RichText/RichText';
import { Heading } from 'ui/components/1-atoms/Heading/Heading';
import { Paragraph } from 'ui/components/1-atoms/Paragraph/Paragraph';
import { Container } from '../Container/Container';
import { ArticleDate } from 'ui/components/2-molecules/Article/ArticleDate/ArticleDate';
import { ArticleMedia, ArticleMediaProps } from 'ui/components/2-molecules/Article/ArticleMedia/ArticleMedia';
import { ArticleTextProps } from 'ui/components/2-molecules/Article/ArticleText/ArticleText';
import { LightBgColor } from 'helpers/types';
import { useState } from 'react';
import { OrttoForm } from 'ui/components/2-molecules/OrttoForm/OrttoForm';

export interface ArticleProps {
	className?: string;
	heroCard?: HeroCardProps;
	articleAuthors?: ArticleAuthorProps[];
	showArticleAuthorsBio?: boolean;
	articleAuthorHeading?: string;
	articleInfo?: ArticleInfoProps;
	articleTeaser?: string;
	articleHeading?: string;
	articleTeaserHeading?: string;
	articleDate?: string;
	articleDateStatus?: string;
	richTextSections?: ArticleTextProps[] | ArticleMediaProps[];
	articleShowFormButtonText?: string;
	layoutType?: 'Article' | 'Event';
	articleBgColor?: LightBgColor;
	formHtmlCode?: string;
	eventDisplayButtonRight?: boolean;
	placeholderLabel?: string;
}

export const Article: React.FC<ArticleProps> = ({
	className,
	heroCard,
	articleAuthors,
	showArticleAuthorsBio,
	articleAuthorHeading,
	articleInfo,
	articleTeaser,
	articleTeaserHeading,
	articleHeading,
	richTextSections,
	articleDate,
	layoutType = 'Article',
	articleBgColor,
	articleShowFormButtonText,
	formHtmlCode,
	articleDateStatus,
	eventDisplayButtonRight,
	placeholderLabel,
}) => {
	const [showForm, setShowForm] = useState(false);
	const onButtonClick = () => {
		setShowForm(true);
	};

	return layoutType === 'Article' ? (
		<>
			<div className={classNames(styles.Article, className)}>
				{heroCard?.heading && <HeroCard className={styles.Article_heroCard} {...heroCard} />}

				<Container width="Standard" fillWidth>
					<TopLevelGrid wrap>
						<TopLevelGridCell desktopWidth="8col" mobileWidth="100" className={styles.Article_authorsContainer}>
							{articleAuthorHeading && (
								<Paragraph bold className={styles.Article_articleAuthorHeading}>
									{articleAuthorHeading}
								</Paragraph>
							)}
							{articleAuthors &&
								articleAuthors.map((articleAuthor, index) => (
									<ArticleAuthor key={index} className={styles.Article_articleAuthor} {...articleAuthor} />
								))}

							{articleDate && <ArticleDate date={articleDate} dateStatus={articleDateStatus} className={styles.Article_articleDate} />}
						</TopLevelGridCell>

						<TopLevelGridCell desktopWidth="12col" mobileWidth="100" className={styles.Article_content}>
							{articleInfo && <ArticleInfo className={styles.Article_articleInfo} {...articleInfo} />}
							{articleTeaser && <p className={styles.Article_articleTeaser}>{articleTeaser}</p>}
							{articleHeading && (
								<Heading headingLevel="h2" style="md" className={styles.Article_articleHeading___article}>
									{articleHeading}
								</Heading>
							)}
							{richTextSections &&
								richTextSections.map((section, index) => {
									if (section && section.documentType === 'articleMedia') {
										return (
											<div className={styles.Article_mediaSectionContainer} key={index}>
												<div className={styles.Article_imageSection}>
													<ArticleMedia key={index} className={styles.Article_image} {...section} />
												</div>
											</div>
										);
									} else {
										return (
											<div className={styles.Article_richTextSectionContainer} key={index}>
												<RichText
													key={index}
													className={styles.Article_richTextSection}
													content={section.content}
													readMoreButtonText={section.readMoreButtonText}
													readMoreText={section.readMoreText}
												/>
											</div>
										);
									}
								})}
						</TopLevelGridCell>
					</TopLevelGrid>
				</Container>
			</div>
		</>
	) : (
		<>
			<div className={classNames(styles.Article, className)}>
				{heroCard?.heading && <HeroCard {...heroCard} />}
				<div>
					<Container width="Standard" fillWidth>
						<TopLevelGrid
							wrap
							className={classNames(styles.Article_boxInfo, {
								[styles['Article--articleBgColorGrey']]: articleBgColor === LightBgColor.Grey,
								[styles['Article--articleBgColorLilac']]: articleBgColor === LightBgColor.Lilac,
								[styles['Article--articleBgColorTurqoise']]: articleBgColor === LightBgColor.Turqoise,
								[styles['Article--articleBgColorPink']]: articleBgColor === LightBgColor.Pink,
								[styles['Article--articleBgColorSand']]: articleBgColor === LightBgColor.Sand,
								[styles['Article--articleBgColorLightBlue']]: articleBgColor === LightBgColor.LightBlue,
							})}
						>
							<TopLevelGridCell desktopWidth="8col" mobileWidth="100" className={classNames(styles.Article_authorsContainer___event)}>
								{articleAuthorHeading && (
									<Paragraph bold className={styles.Article_articleAuthorHeading}>
										{articleAuthorHeading}
									</Paragraph>
								)}
								{articleAuthors &&
									articleAuthors.map((articleAuthor, index) => (
										<ArticleAuthor
											key={index}
											className={styles.Article_articleAuthor}
											showBio={showArticleAuthorsBio}
											{...articleAuthor}
										/>
									))}

								{articleDate && (
									<ArticleDate date={articleDate} dateStatus={articleDateStatus} className={styles.Article_articleDate} />
								)}
							</TopLevelGridCell>

							<TopLevelGridCell desktopWidth="16col" mobileWidth="100" className={classNames(styles.Article_content___event)}>
								{articleTeaserHeading && (
									<Paragraph bold className={styles.Article_articleAuthorHeading}>
										{articleTeaserHeading}
									</Paragraph>
								)}
								{articleTeaser && <p className={styles.Article_articleTeaser}>{articleTeaser}</p>}
								{articleInfo && (
									<ArticleInfo
										className={styles.Article_articleInfo}
										{...articleInfo}
										button={
											articleShowFormButtonText && formHtmlCode
												? { text: articleShowFormButtonText, onClick: onButtonClick, showButton: showForm }
												: undefined
										}
									/>
								)}

								{showForm && (
									<TopLevelGrid wrap nogutter>
										<TopLevelGridCell desktopWidth="16col" mobileWidth="100">
											<OrttoForm
												className={styles.Article_formContainer}
												formHtmlCode={formHtmlCode}
												eventDisplayButtonRight={eventDisplayButtonRight}
												placeholderLabel={placeholderLabel}
											></OrttoForm>
										</TopLevelGridCell>
									</TopLevelGrid>
								)}
							</TopLevelGridCell>
						</TopLevelGrid>
					</Container>
				</div>
				<div>
					<Container width="Standard" fillWidth>
						<TopLevelGrid wrap nogutter>
							<TopLevelGridCell desktopWidth="8col" mobileWidth="100">{``}</TopLevelGridCell>
							<TopLevelGridCell
								desktopWidth="12col"
								mobileWidth="100"
								className={classNames(styles.Article_content___eventDescription)}
							>
								{articleHeading && (
									<Heading headingLevel="h2" style="md" className={classNames(styles.Article_articleHeading)}>
										{articleHeading}
									</Heading>
								)}
								{richTextSections &&
									richTextSections.map((section, index) => {
										if (section && section.documentType === 'articleMedia') {
											return (
												<div className={styles.Article_mediaSectionContainer} key={index}>
													<div className={styles.Article_imageSection}>
														<ArticleMedia key={index} className={styles.Article_image} {...section} />
													</div>
												</div>
											);
										} else {
											return (
												<div className={styles.Article_richTextSectionContainer} key={index}>
													<RichText
														key={index}
														className={styles.Article_richTextSection}
														content={section.content}
														readMoreButtonText={section.readMoreButtonText}
														readMoreText={section.readMoreText}
													/>
												</div>
											);
										}
									})}
							</TopLevelGridCell>
						</TopLevelGrid>
					</Container>
				</div>
			</div>
		</>
	);
};
